import React, { useState, useRef } from 'react';
import { bool, func, string, number, arrayOf } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from '../../util/reactIntl';

import css from './WishlistImageUpload.module.css';

const WishlistImageUpload = props => {
  const intl = useIntl();
  const {
    className,
    rootClassName,
    existingImageUrl,
    onImageSelected,
    onImageRemoved,
    maxSizeBytes,
    acceptedFormats,
  } = props;

  const [dragActive, setDragActive] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(existingImageUrl || null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const classes = classNames(rootClassName || css.root, className);
  const dropZoneClasses = classNames(css.dropZone, {
    [css.dragActive]: dragActive,
    [css.hasImage]: previewUrl,
  });

  const validateFile = file => {
    // Check file type
    if (acceptedFormats && !acceptedFormats.includes(file.type)) {
      return 'WishlistImageUpload.invalidFileType';
    }

    // Check file size
    if (maxSizeBytes && file.size > maxSizeBytes) {
      const maxMB = Math.round(maxSizeBytes / (1024 * 1024));
      return { id: 'WishlistImageUpload.fileTooLarge', values: { max: maxMB } };
    }

    return null;
  };

  const handleFileSelect = file => {
    setError(null);

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    // Create preview URL
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    // Notify parent component
    if (onImageSelected) {
      onImageSelected(file);
    }
  };

  const handleDrag = e => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = e => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleChange = e => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemove = () => {
    setPreviewUrl(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (onImageRemoved) {
      onImageRemoved();
    }
  };

  return (
    <div className={classes}>
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedFormats?.join(',') || 'image/jpeg,image/png'}
        onChange={handleChange}
        className={css.fileInput}
      />

      <div
        className={dropZoneClasses}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={!previewUrl ? handleClick : undefined}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (!previewUrl && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            handleClick();
          }
        }}
      >
        {previewUrl ? (
          <div className={css.preview}>
            <img
              src={previewUrl}
              alt={intl.formatMessage({ id: 'WishlistImageUpload.previewAlt' })}
              className={css.previewImage}
            />
            <div className={css.previewOverlay}>
              <button
                type="button"
                className={css.removeButton}
                onClick={e => {
                  e.stopPropagation();
                  handleRemove();
                }}
              >
                <FormattedMessage id="WishlistImageUpload.removeImage" />
              </button>
              <button
                type="button"
                className={css.changeButton}
                onClick={e => {
                  e.stopPropagation();
                  handleClick();
                }}
              >
                <FormattedMessage id="WishlistImageUpload.changeImage" />
              </button>
            </div>
          </div>
        ) : (
          <div className={css.uploadPrompt}>
            <div className={css.uploadIcon}>+</div>
            <p className={css.uploadText}>
              <FormattedMessage id="WishlistImageUpload.addPhoto" />
            </p>
            <p className={css.uploadHint}>
              <FormattedMessage
                id="WishlistImageUpload.fileRequirements"
                values={{
                  formats: '.JPG, .PNG',
                  maxSize: maxSizeBytes ? Math.round(maxSizeBytes / (1024 * 1024)) : 20,
                }}
              />
            </p>
          </div>
        )}
      </div>

      {error && (
        <div className={css.error}>
          {typeof error === 'string' ? (
            <FormattedMessage id={error} />
          ) : (
            <FormattedMessage id={error.id} values={error.values} />
          )}
        </div>
      )}
    </div>
  );
};

WishlistImageUpload.defaultProps = {
  className: null,
  rootClassName: null,
  existingImageUrl: null,
  onImageSelected: null,
  onImageRemoved: null,
  maxSizeBytes: 20 * 1024 * 1024, // 20MB
  acceptedFormats: ['image/jpeg', 'image/jpg', 'image/png'],
};

WishlistImageUpload.propTypes = {
  className: string,
  rootClassName: string,
  existingImageUrl: string,
  onImageSelected: func,
  onImageRemoved: func,
  maxSizeBytes: number,
  acceptedFormats: arrayOf(string),
};

export default WishlistImageUpload;
